import RoundButton from '@oberoninternal/travelbase-ds/components/action/RoundButton';
import ArrowLeft from '@oberoninternal/travelbase-ds/components/figure/ArrowLeft';
import ArrowRight from '@oberoninternal/travelbase-ds/components/figure/ArrowRight';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import { debounce } from 'debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CarouselGrid, Cell } from './designsystem/WideGrid';

interface Props {
    tiles: React.ReactNode[];
    onLoadMore?: () => Promise<unknown>;
    canLoadMore?: boolean;
    loadingIndicator?: React.ReactNode;
    large?: boolean;
}

const findClosestOffset = (scrollLeft: number, tiles: NodeListOf<HTMLDivElement>): number | undefined => {
    let lastClosestIndex;
    let lastClosest = Number.MAX_SAFE_INTEGER;

    tiles.forEach((t, i) => {
        const o = Math.abs(t.offsetLeft + parseFloat(getComputedStyle(t).paddingLeft) - scrollLeft);
        if (o < lastClosest) {
            lastClosestIndex = i;
            lastClosest = o;
        }
    });

    return lastClosestIndex !== undefined
        ? tiles[lastClosestIndex].offsetLeft + parseFloat(getComputedStyle(tiles[lastClosestIndex]).paddingLeft)
        : undefined;
};

const TileCarousel: React.FC<React.PropsWithChildren<Props>> = ({
    tiles,
    onLoadMore,
    loadingIndicator,
    canLoadMore,
    large,
}) => {
    const shouldRenderLoadingIndicator = !!loadingIndicator && canLoadMore;
    const device = useDeviceSize();
    const container = useRef<HTMLDivElement | null>(null);
    const onPrev = useCallback(() => {
        const c = container.current;
        if (c) {
            c.scrollTo({
                left: findClosestOffset(c.scrollLeft - c.clientWidth, c.querySelectorAll('[data-cell]')),
                behavior: 'smooth',
            });
        }
    }, []);
    const onNext = useCallback(() => {
        const c = container.current;
        if (c) {
            c.scrollTo({
                left: findClosestOffset(c.scrollLeft + c.clientWidth, c.querySelectorAll('[data-cell]')),
                behavior: 'smooth',
            });
        }
    }, []);

    const [prevEnabled, setPrevEnabled] = useState(false);
    const [nextEnabled, setNextEnabled] = useState(false);
    const [loading, setLoading] = useState(false);

    const performOnLoadMore = useCallback(async () => {
        if (!onLoadMore || loading || !canLoadMore) {
            return;
        }
        setLoading(true);
        await onLoadMore();
        setLoading(false);
        setNextEnabled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canLoadMore, loading]);

    useEffect(() => {
        const el = container.current;
        if (!el) {
            return undefined;
        }
        const handler = debounce(() => {
            const elScrollPos = Math.round(el.scrollLeft + el.clientWidth);
            const reachedEnd = elScrollPos === el.scrollWidth;
            if (elScrollPos >= el.scrollWidth - (el.clientWidth + 100)) {
                performOnLoadMore();
            }
            setNextEnabled(!reachedEnd);
            setPrevEnabled(el.scrollLeft > 0);
        });

        handler();

        el.addEventListener('scroll', handler);

        return () => el.removeEventListener('scroll', handler);
    }, [performOnLoadMore]);

    return (
        <Container>
            <NavButtons>
                <RoundButton
                    disabled={!prevEnabled}
                    variant="outline"
                    onClick={onPrev}
                    size={device === 'mobile' ? 'small' : 'medium'}
                >
                    <ArrowLeft />
                </RoundButton>
                <RoundButton
                    disabled={!nextEnabled}
                    variant="outline"
                    onClick={onNext}
                    size={device === 'mobile' ? 'small' : 'medium'}
                >
                    <ArrowRight />
                </RoundButton>
            </NavButtons>
            <Carousel ref={container}>
                <CarouselGrid
                    length={tiles.length + (shouldRenderLoadingIndicator ? 1 : 0)}
                    widths={[100, 50, large ? 50 : 100 / 3]}
                >
                    {tiles.map((tile, i) => (
                        <Cell data-cell key={i}>
                            {tile}
                        </Cell>
                    ))}
                    {shouldRenderLoadingIndicator && (
                        <Cell style={{ scrollSnapAlign: 'none' }} data-cell>
                            {loadingIndicator}
                        </Cell>
                    )}
                </CarouselGrid>
            </Carousel>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
`;

const Carousel = styled.div`
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -ms-overflow-style: none;
    overscroll-behavior-x: none;
    will-change: transform;

    /* make it smooth on iOS */
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const NavButtons = styled.div`
    position: absolute;
    top: -6.8rem;
    right: 0;

    & > * + * {
        margin-left: 0.8rem;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        top: -6.8rem;
    }
`;

export default TileCarousel;
